import i18next from 'i18next';
import type { i18n, ResourceKey, TOptions } from 'i18next';

export type I18NInstance<Translations> = i18n & {
  t: (key: keyof Translations, options: TOptions) => string;
  interpolate: (str: string, data: object) => string;
};

export type CustomFormatters = Record<
  string,
  (lng?: string | undefined, options?: any) => (value: any) => string
>;

export default function createI18nInstance<Translations>(
  defaults: Translations,
  config?: Translations,
  locale: string = 'en',
  customFormatters?: CustomFormatters
): I18NInstance<Translations> {
  const resolvedTranslations = { ...defaults, ...config };
  const fallbackLng = 'cimode';

  // HINT: For easy testing—setting lng to 'cimode' will cause the t function to always return the key.
  // docs: https://www.i18next.com/overview/api#changelanguage
  const lng =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('translationKeys') ===
      'true'
      ? fallbackLng
      : locale;

  const instance = i18next.createInstance(
    {
      debug: false,
      lng,
      fallbackLng,
      resources: {
        [lng]: {
          translation: resolvedTranslations as ResourceKey
        }
      }
    },
    (err) => {
      if (err) throw err;
    }
  );

  Object.entries(customFormatters ?? {}).forEach(([name, func]) => {
    instance.services.formatter?.addCached(name, func);
  });

  return {
    ...instance,
    interpolate(str: string, data: object) {
      return instance.services.interpolator.interpolate(
        str,
        data,
        instance.language,
        {}
      );
    }
  } as I18NInstance<Translations>;
}
